import { Mail, Phone, MapPin } from "lucide-react";
import { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { API_URL } from "../../../../config/api";

export default function ContactPage() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: ""
    });

    const [status, setStatus] = useState({
        loading: false,
        message: "",
        type: ""
    });

    const recaptchaRef = useRef(null);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    // OTP State
    const [isVerified, setIsVerified] = useState(false);
    const [otpMode, setOtpMode] = useState(false);
    const [otp, setOtp] = useState("");
    const [otpStatus, setOtpStatus] = useState({ loading: false, message: "", type: "" });

    const handleSendOTP = async () => {
        if (!formData.email) {
            setOtpStatus({ loading: false, message: "Please enter your email address first.", type: "error" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setOtpStatus({ loading: false, message: "Please enter a valid email address.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/request-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier: formData.email, context: "contact" })
            });

            // Safely parse response - handle non-JSON responses (e.g. HTML error pages)
            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setOtpMode(true);
                setOtpStatus({ loading: false, message: "Verification code sent to your email.", type: "success" });
            } else {
                setOtpStatus({ loading: false, message: data.message || "Failed to send code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Send Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length < 6) {
            setOtpStatus({ loading: false, message: "Please enter a valid 6-digit code.", type: "error" });
            return;
        }

        setOtpStatus({ loading: true, message: "", type: "" });
        try {
            const res = await fetch(`${API_URL}/auth/verify-otp`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email, otp, identifier: formData.email })
            });

            // Safely parse response - handle non-JSON responses
            let data;
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
                data = await res.json();
            } else {
                const text = await res.text();
                console.error("Non-JSON response:", res.status, text.substring(0, 200));
                data = { success: false, message: `Server error (${res.status}). Please try again.` };
            }

            if (data.success) {
                setIsVerified(true);
                setOtpMode(false);
                setOtpStatus({ loading: false, message: "Email verified successfully!", type: "success" });
                setTimeout(() => setOtpStatus({ loading: false, message: "", type: "" }), 3000);
            } else {
                setOtpStatus({ loading: false, message: data.message || "Invalid or expired code.", type: "error" });
            }
        } catch (error) {
            console.error("❌ OTP Verify Error:", error);
            const msg = error instanceof TypeError ? "Cannot connect to server. Please check your connection." : (error.message || "Network Error");
            setOtpStatus({ loading: false, message: msg, type: "error" });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isVerified) {
            setStatus({ loading: false, message: "Please verify your email address before sending.", type: "error" });
            return;
        }

        if (!recaptchaToken) {
            setStatus({ loading: false, message: "Please complete the reCAPTCHA verification.", type: "error" });
            return;
        }

        setStatus({ loading: true, message: "", type: "" });

        const baseUrl = API_URL;

        try {
            const res = await fetch(`${baseUrl}/email/sendEmail`, {
                method: "POST",
                body: JSON.stringify({
                    email: formData.email,
                    subject: "Contact Form Submission",
                    message: formData.message,
                    name: formData.name,
                    recaptchaToken
                }),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            // Get text first to handle non-JSON errors
            const responseText = await res.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (e) {
                console.error("Failed to parse JSON response:", responseText);
                throw new Error(`Server returned non-JSON response: ${res.status} ${res.statusText}`);
            }

            if (res.ok) {
                setStatus({
                    loading: false,
                    message: "Message sent successfully! We'll get back to you soon.",
                    type: "success"
                });
                setFormData({ name: "", email: "", message: "" });
                setRecaptchaToken("");
                setIsVerified(false);
                setOtpMode(false);
                setOtp("");

                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }

                setTimeout(() => {
                    setStatus({ loading: false, message: "", type: "" });
                }, 5000);
            } else {
                throw new Error(data.message || "Failed to send message");
            }
        } catch (error) {
            console.error("Error:", error);
            setStatus({
                loading: false,
                message: error.message || "Failed to send message. Please try again.",
                type: "error"
            });
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="min-h-screen py-16 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                        Get In Touch
                    </h1>
                    <p className="text-gray-300 md:text-lg">
                        We&apos;d love to hear from you. Send us a message and we&apos;ll respond as soon as possible.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-10 overflow-hidden group hover:border-purple-500/30 transition-all duration-500">
                            {/* Decorative gradient orb */}
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>

                            <h2 className="text-2xl md:text-2xl font-bold text-white mb-10 relative">
                                Contact
                                {/* <div className="absolute -bottom-2 left-0 w-20 h-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"></div> */}
                            </h2>

                            <div className="space-y-6 relative">
                                {/* Location */}
                                <div className="flex items-start gap-5 group/item">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-md opacity-0 group-hover/item:opacity-70 transition-opacity duration-300"></div>
                                        <div className="relative p-4 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600">
                                            <MapPin className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1 pt-1">
                                        <h3 className="text-lg font-semibold text-white mb-2">
                                            Location
                                        </h3>
                                        <p className="text-gray-300 text-base leading-relaxed">Melbourne, Australia</p>
                                    </div>
                                </div>

                                {/* Divider */}
                                {/* <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div> */}

                                {/* Email */}
                                <div className="flex items-start gap-5 group/item">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-md opacity-0 group-hover/item:opacity-70 transition-opacity duration-300"></div>
                                        <div className="relative p-4 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600">
                                            <Mail className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1 pt-1">
                                        <h3 className="text-lg font-semibold text-white mb-2">
                                            Email
                                        </h3>
                                        <a
                                            href="mailto:enquiries@sanmicron.com"
                                            className="text-gray-300 text-base inline-flex items-center gap-2 group/link break-all"
                                        >
                                            enquiries@sanmicron.com
                                        </a>
                                    </div>
                                </div>

                                {/* Divider */}
                                {/* <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div> */}

                                {/* Phone */}
                                <div className="flex items-start gap-5 group/item">
                                    <div className="relative">
                                        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl blur-md opacity-0 group-hover/item:opacity-70 transition-opacity duration-300"></div>
                                        <div className="relative p-4 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600">
                                            <Phone className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                    <div className="flex-1 pt-1">
                                        <h3 className="text-lg font-semibold text-white mb-2 transition-all duration-300">
                                            Phone
                                        </h3>
                                        <a
                                            href="tel:+61391167215"
                                            className="text-gray-300 text-base hover:text-gray-300"
                                        >
                                            +61 391 167 215
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Business Hours Card */}
                        <div className="relative bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-10 overflow-hidden group hover:border-blue-500/30 transition-all duration-500">
                            {/* Decorative gradient orb */}
                            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>

                            <div className="relative">
                                <h3 className="text-2xl font-bold text-white mb-0 inline-block">
                                    Business Hours
                                    {/* <div className="absolute -bottom-2 left-0 w-16 h-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full"></div> */}
                                </h3>

                                <div className="space-y-3 mt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600"></div>
                                        <span className="text-gray-300">Monday - Friday: 9:00 AM - 6:00 PM</span>
                                    </div>

                                    <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-blue-600"></div>
                                        <span className="text-gray-300">Saturday - Sunday : 10:00 AM - 2:00 PM</span>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 md:p-8">
                        <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-6">Send us a message</h3>
                        <form onSubmit={handleSubmit} className="space-y-7">
                            <div>
                                <label htmlFor="name" className="block font-medium text-gray-300 mb-3">
                                    Your Name
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    placeholder="John Doe"
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors disabled:opacity-50"
                                />
                            </div>
                            <div className="mb-4">
                                <label htmlFor="email" className="block font-medium text-gray-300 mb-3">
                                    Your Email
                                </label>
                                <div className="relative group">
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setIsVerified(false);
                                            setOtpMode(false);
                                        }}
                                        required
                                        disabled={status.loading || isVerified}
                                        placeholder="john@example.com"
                                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors disabled:opacity-50 pr-24"
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                                        {formData.email && !isVerified && !otpMode && (
                                            <button
                                                type="button"
                                                onClick={handleSendOTP}
                                                disabled={otpStatus.loading}
                                                className="flex items-center justify-center w-9 h-9 bg-cyan-500/10 hover:bg-cyan-500 text-cyan-400 hover:text-white rounded-lg border border-cyan-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                title="Verify Email"
                                            >
                                                {otpStatus.loading ? (
                                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                )}
                                            </button>
                                        )}
                                        {(isVerified || otpMode) && (
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 shadow-[0_0_10px_rgba(34,197,94,0.2)] animate-in fade-in zoom-in duration-500">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {otpMode && !isVerified && (
                                <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
                                    <label className="block text-sm text-gray-300 mb-3">Enter Verification Code</label>
                                    <div className="flex gap-3">
                                        <input
                                            type="text"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            placeholder="000000"
                                            maxLength={6}
                                            className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white text-center tracking-widest text-lg focus:outline-none focus:border-cyan-400"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleVerifyOTP}
                                            disabled={otpStatus.loading || otp.length < 6}
                                            className="px-6 py-3 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 font-medium rounded-lg transition-colors border border-blue-500/50 disabled:opacity-50"
                                        >
                                            {otpStatus.loading ? "Checking..." : "Confirm"}
                                        </button>
                                    </div>
                                    {otpStatus.message && (
                                        <p className={`text-sm mt-3 ${otpStatus.type === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                                            {otpStatus.message}
                                        </p>
                                    )}
                                </div>
                            )}

                            {!otpMode && otpStatus.message && otpStatus.type === 'error' && (
                                <p className="text-sm text-red-400 mb-4 bg-red-500/10 p-3 rounded-lg border border-red-500/20">{otpStatus.message}</p>
                            )}
                            <div>
                                <label htmlFor="message" className="block font-medium text-gray-300 mb-3">
                                    Your Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    disabled={status.loading}
                                    placeholder="Tell us about your project..."
                                    rows={6}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 transition-colors resize-none disabled:opacity-50"
                                />
                            </div>

                            {status.message && (
                                <div className={`p-4 rounded-lg ${status.type === 'success'
                                    ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                                    : 'bg-red-500/20 border border-red-500/50 text-red-400'
                                    }`}>
                                    {status.message}
                                </div>
                            )}

                            <div className="flex justify-center w-full">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    theme="dark"
                                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"}
                                    onChange={(val) => setRecaptchaToken(val)}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={status.loading}
                                className="w-full px-6 py-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-cyan-500 hover:to-blue-600 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                            >
                                {status.loading ? "Sending..." : "Send Message"}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}